import React, { memo, useCallback, useMemo } from 'react'
import { GridColumnVisibilityModel, GridPaginationModel } from '@mui/x-data-grid-pro'
import { useSelector } from 'react-redux'
import { EmptyWebhookDeliveries } from 'src/components/webhook/grid/EmptyWebhookDeliveries'
import { GridTable } from 'src/components/shared/grid/GridTable'
import {
  WEBHOOK_DELIVERIES_GRID_COLUMNS_STORAGE_KEY,
  webhookDeliveryPageSizeOptions,
} from 'src/constants/webhook/constants'
import { WebhookDeliveriesTableFields } from 'src/constants/webhook/enums'
import { buildWebhookDeliveryColumns } from 'src/constants/webhook/webhookDeliveriesGrid'
import type { WebhookDelivery } from 'src/interfaces/webhook'
import {
  getWebhookDeliveries,
  getWebhookDeliveriesLoading,
  getWebhookDeliveriesTotal,
} from 'src/selectors/webhook'
import { useGridColumns } from 'src/utils/hooks/grid/useGridColumns'

interface Props {
  paginationModel: GridPaginationModel
  onPaginationModelChange(model: GridPaginationModel): void
  hasFilters: boolean
  onViewDelivery(delivery: WebhookDelivery): void
}

const getRowId = (row: WebhookDelivery) => {
  return row.id
}

// The result chip and the actions column have no meaning when hidden; both also carry
// `hideable: false`, which removes them from the manage-columns panel. This guard additionally
// stops a programmatic visibility-model change from persisting a hide.
const UNHIDEABLE_COLUMNS: Array<string> = [
  WebhookDeliveriesTableFields.deliveryStatus,
  WebhookDeliveriesTableFields.actions,
]

const WebhookDeliveriesTableComponent = ({
  paginationModel,
  onPaginationModelChange,
  hasFilters,
  onViewDelivery,
}: Props) => {
  const deliveries = useSelector(getWebhookDeliveries)
  const deliveriesTotal = useSelector(getWebhookDeliveriesTotal)
  const isLoading = useSelector(getWebhookDeliveriesLoading)

  const deliveryColumns = useMemo(() => {
    return buildWebhookDeliveryColumns(onViewDelivery)
  }, [onViewDelivery])

  const { includedColumns, handleAddExcludedColumn, handleRemoveExcludedColumn } = useGridColumns(
    deliveryColumns,
    WEBHOOK_DELIVERIES_GRID_COLUMNS_STORAGE_KEY,
  )

  const columnVisibilityModel = useMemo(() => {
    return deliveryColumns.reduce<GridColumnVisibilityModel>((accumulator, column) => {
      accumulator[column.field] = includedColumns.includes(column.field)

      return accumulator
    }, {})
  }, [deliveryColumns, includedColumns])

  // `useGridColumns` stores excluded fields as a flat list; the grid speaks a visibility model.
  // Translate both ways so menu changes persist to localStorage across reloads.
  const handleColumnVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      deliveryColumns.forEach((column) => {
        if (UNHIDEABLE_COLUMNS.includes(column.field)) {
          return
        }

        if (model[column.field] === false) {
          handleAddExcludedColumn(column.field)

          return
        }

        handleRemoveExcludedColumn(column.field)
      })
    },
    [deliveryColumns, handleAddExcludedColumn, handleRemoveExcludedColumn],
  )

  // `GridTable` renders EmptyElement with no props, so the filter-aware copy is bound here.
  const EmptyElement = useMemo(() => {
    const BoundEmptyWebhookDeliveries = () => {
      return <EmptyWebhookDeliveries hasFilters={hasFilters} />
    }

    return BoundEmptyWebhookDeliveries
  }, [hasFilters])

  // Server pagination: `rowCount` is the envelope's `total`, which the backend computes with
  // getCount() BEFORE paging, so it is a true unfiltered-by-page count. `GridTable` flips to
  // paginationMode: 'server' on its own once a paginationModel is passed, and spreads rowCount
  // through — no shared-component change needed.
  return (
    <GridTable<WebhookDelivery>
      rows={deliveries}
      columns={deliveryColumns}
      getRowId={getRowId}
      isLoading={isLoading}
      rowCount={deliveriesTotal}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      pageSizeOptions={webhookDeliveryPageSizeOptions}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
      EmptyElement={EmptyElement}
      tableDataTestId="webhook-deliveries-table"
      rowDataTestIdPrefix="webhook-deliveries"
    />
  )
}

export const WebhookDeliveriesTable = memo(WebhookDeliveriesTableComponent)
